import React, { useCallback, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Image, Platform, Dimensions, Modal as RNModal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from './DirectionalIcon';
import { colors, spacing, typography } from '../theme';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { useConversationHandler } from '../hooks/useConversationHandler';
import { getImageUrlSync } from '../utils/imageProxy';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { ContactQuickActions } from './ContactQuickActions';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');
const isWebPlatform = Platform.OS === 'web';

/** Mirrors ContactsList.tsx's/ContactEditForm.tsx's/PhoneCallScreen.tsx's
 * identical helper — converts a touch event's document-relative pageX/pageY
 * into window-relative coordinates for a fixed-position web Modal. */
const toWindowRelative = (pageX: number, pageY: number): { x: number; y: number } => {
  if (!isWebPlatform || typeof window === 'undefined') return { x: pageX, y: pageY };
  return { x: pageX - window.scrollX, y: pageY - window.scrollY };
};

// Channels that open an existing conversation when tapped — mirrors
// PhoneCallScreen.tsx's identical restriction (only what
// createOrFindConversation actually supports).
const OPENABLE_CHANNELS = new Set(['whatsapp', 'facebook-messenger']);

const getChannelIcon = (channel: string): any => {
  switch (channel) {
    case 'whatsapp': return 'logo-whatsapp';
    case 'facebook-messenger': return 'logo-facebook';
    case 'telegram': return 'paper-plane';
    case 'email': return 'mail';
    case 'instagram': return 'logo-instagram';
    case 'phone': return 'call';
    default: return 'chatbubble';
  }
};

const getChannelColor = (channel: string): string => {
  switch (channel) {
    case 'whatsapp': return '#25D366';
    case 'facebook-messenger': return '#1877F2';
    case 'telegram': return '#0088CC';
    case 'email': return '#EA4335';
    case 'instagram': return '#E4405F';
    case 'phone': return '#0EA5E9';
    default: return colors.primary as string;
  }
};

export interface ContactSummaryCardProps {
  contact: any;
  contactName: string;
  loadingContact?: boolean;
  onNotify: (type: 'success' | 'error' | 'warning' | 'info', title: string, message: string) => void;
}

/**
 * Contact identity + helper actions — name/avatar (tappable to open the
 * contact's own screen), call/chat icons (each opening a picker when the
 * contact has more than one number/identity), and the Reminder/Add-to-
 * List/Assign quick actions. Extracted from VisitDetailScreen (its original
 * home) once VisitExecuteScreen needed the identical block too — shared here
 * rather than duplicated a second time.
 */
export const ContactSummaryCard: React.FC<ContactSummaryCardProps> = ({ contact, contactName, loadingContact, onNotify }) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, startPhoneCall, isPhoneDialerReady } = useAppContext();
  const { startConversation } = useConversationHandler();
  const { t } = useLocale();

  const [phonePickerNumbers, setPhonePickerNumbers] = useState<Array<{ value: string; index: number }> | null>(null);
  const [phonePickerPosition, setPhonePickerPosition] = useState<{ x: number; y: number } | null>(null);
  const [channelPickerType, setChannelPickerType] = useState<string | null>(null);
  const [channelPickerPosition, setChannelPickerPosition] = useState<{ x: number; y: number } | null>(null);

  const handleCallPress = useCallback(async (phoneIndex?: number) => {
    if (!contact?._id) return;
    if (!isPhoneDialerReady) {
      console.warn('[ContactSummaryCard] Call button pressed before phone dialer is ready — ignoring press');
      return;
    }
    try {
      const res = await apiService.getContactDialTarget(contact._id, undefined, undefined, undefined, phoneIndex);
      const phoneNumber = res.data?.phoneNumber;
      if (!phoneNumber) return;
      startPhoneCall(phoneNumber, contact._id, { name: contactName, avatar: contact.profile?.avatar });
    } catch (error) {
      console.error('[ContactSummaryCard] Failed to resolve dial target for contact', contact._id, error);
    }
  }, [contact, contactName, isPhoneDialerReady, startPhoneCall]);

  const handlePhoneIconPress = useCallback((event?: any) => {
    const rawPhones: string[] = contact?.identifiers?.phone || [];
    const seen = new Set<string>();
    const uniquePhones: Array<{ value: string; index: number }> = [];
    rawPhones.forEach((phone, index) => {
      const normalized = phone.replace(/^\+/, '').trim();
      if (!seen.has(normalized)) {
        seen.add(normalized);
        uniquePhones.push({ value: phone, index });
      }
    });
    if (uniquePhones.length > 1) {
      if (event) {
        const { pageX, pageY } = event.nativeEvent ?? {};
        if (typeof pageX === 'number' && typeof pageY === 'number') {
          setPhonePickerPosition(toWindowRelative(pageX, pageY));
        }
      }
      setPhonePickerNumbers(uniquePhones);
    } else {
      handleCallPress();
    }
  }, [contact, handleCallPress]);

  const handleChannelPress = useCallback((channel: string, channelIdentifier?: string) => {
    if (!OPENABLE_CHANNELS.has(channel) || !contact?._id) return;
    startConversation({
      id: contact._id,
      name: contactName,
      channel: channel as 'whatsapp' | 'facebook-messenger',
      phoneNumber: channelIdentifier,
    } as any);
  }, [contact, contactName, startConversation]);

  const handleChannelIconPress = useCallback((channel: string, hasMultiple: boolean, event?: any) => {
    if (hasMultiple) {
      if (event) {
        const { pageX, pageY } = event.nativeEvent ?? {};
        if (typeof pageX === 'number' && typeof pageY === 'number') {
          setChannelPickerPosition(toWindowRelative(pageX, pageY));
        }
      }
      setChannelPickerType(channel);
    } else {
      handleChannelPress(channel);
    }
  }, [handleChannelPress]);

  return (
    <View style={styles.card}>
      <TouchableOpacity
        style={styles.contactRow}
        onPress={() => contact?._id && navigateTo('contacts')}
        disabled={!contact?._id}
      >
        {contact?.profile?.avatar ? (
          <Image source={{ uri: getImageUrlSync(contact.profile.avatar, apiService.getBaseURL?.() ?? null) }} style={styles.contactAvatar} />
        ) : (
          <View style={[styles.contactAvatar, styles.contactAvatarFallback]}>
            <Text style={styles.contactAvatarInitials}>{contactName.charAt(0).toUpperCase()}</Text>
          </View>
        )}
        <View style={styles.contactInfo}>
          <Text style={styles.contactName} numberOfLines={1}>{loadingContact ? '…' : contactName}</Text>
          {!!contact?.profile?.phone && <Text style={styles.metaText} numberOfLines={1}>{contact.profile.phone}</Text>}
        </View>
        {!!contact?._id && <DirectionalIcon name="chevron-forward" size={16} color={colors.text.secondary as string} />}
      </TouchableOpacity>

      {!!contact?._id && (
        <View style={styles.contactActionsRow}>
          {(() => {
            const rawPhones: string[] = contact?.identifiers?.phone || [];
            const uniquePhoneCount = new Set(rawPhones.map((p: string) => p.replace(/^\+/, '').trim())).size;
            return (contact.profile?.phone || rawPhones.length > 0) ? (
              <TouchableOpacity style={styles.contactActionButton} onPress={handlePhoneIconPress}>
                <Ionicons name="call-outline" size={18} color={colors.primary as string} />
                {uniquePhoneCount > 1 && (
                  <View style={styles.contactActionBadge}>
                    <Ionicons name="chevron-down" size={7} color={colors.white as string} />
                  </View>
                )}
              </TouchableOpacity>
            ) : null;
          })()}
          {(() => {
            const linkedChannelContacts: any[] = contact?.linkedChannelContacts || [];
            const byType = linkedChannelContacts.reduce((acc: Record<string, any[]>, lc: any) => {
              if (OPENABLE_CHANNELS.has(lc.channel)) (acc[lc.channel] = acc[lc.channel] || []).push(lc);
              return acc;
            }, {});
            return Object.entries(byType).map(([channelType, group]) => (
              <TouchableOpacity
                key={channelType}
                style={styles.contactActionButton}
                onPress={(e) => handleChannelIconPress(channelType, group.length > 1, e)}
              >
                <Ionicons name={getChannelIcon(channelType)} size={18} color={getChannelColor(channelType)} />
                {group.length > 1 && (
                  <View style={styles.contactActionBadge}>
                    <Ionicons name="chevron-down" size={7} color={colors.white as string} />
                  </View>
                )}
              </TouchableOpacity>
            ));
          })()}
        </View>
      )}

      {!!contact?._id && (
        <View style={styles.quickActionsWrapper}>
          <ContactQuickActions
            contactId={contact._id}
            contactName={contactName}
            phoneNumber={contact.profile?.phone}
            onNotify={onNotify}
            rowStyle={{ marginBottom: 0 }}
          />
        </View>
      )}

      {/* Phone picker — mirrors ContactsList.tsx's/ConversationScreen.tsx's
          identical multi-number picker. */}
      <RNModal transparent visible={!!phonePickerNumbers} animationType="fade" onRequestClose={() => setPhonePickerNumbers(null)}>
        <TouchableOpacity style={styles.pickerBackdrop} activeOpacity={1} onPress={() => setPhonePickerNumbers(null)}>
          <View
            style={[
              styles.pickerCard,
              phonePickerPosition
                ? {
                    top: Math.max(spacing.sm, Math.min(phonePickerPosition.y + 8, screenHeight - (36 + (phonePickerNumbers?.length || 1) * 32))),
                    left: Math.max(spacing.sm, Math.min(phonePickerPosition.x - 100, screenWidth - 220)),
                  }
                : { top: 60, right: spacing.lg },
            ]}
          >
            {(phonePickerNumbers || []).map(({ value, index }) => (
              <TouchableOpacity
                key={index}
                style={styles.pickerRow}
                onPress={() => { setPhonePickerNumbers(null); handleCallPress(index); }}
              >
                <Ionicons name="call-outline" size={14} color={colors.primary as string} />
                <Text style={styles.pickerRowText} numberOfLines={1}>{value}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </RNModal>

      {/* Channel picker — mirrors PhoneCallScreen.tsx's identical
          one-icon-per-type + picker. */}
      <RNModal transparent visible={!!channelPickerType} animationType="fade" onRequestClose={() => setChannelPickerType(null)}>
        <TouchableOpacity style={styles.pickerBackdrop} activeOpacity={1} onPress={() => setChannelPickerType(null)}>
          <View
            style={[
              styles.pickerCard,
              channelPickerPosition
                ? {
                    top: Math.max(spacing.sm, Math.min(channelPickerPosition.y + 8, screenHeight - 160)),
                    left: Math.max(spacing.sm, Math.min(channelPickerPosition.x - 100, screenWidth - 220)),
                  }
                : { top: 60, right: spacing.lg },
            ]}
          >
            {((contact?.linkedChannelContacts || []) as any[])
              .filter((lc) => lc.channel === channelPickerType)
              .map((lc, index) => (
                <TouchableOpacity
                  key={lc.channelContactId || index}
                  style={styles.pickerRow}
                  onPress={() => { setChannelPickerType(null); handleChannelPress(lc.channel, lc.channelIdentifier); }}
                >
                  <Ionicons name={getChannelIcon(lc.channel)} size={14} color={getChannelColor(lc.channel)} />
                  <Text style={styles.pickerRowText} numberOfLines={1}>{lc.channelIdentifier}</Text>
                  {lc.isPrimary && <Text style={styles.pickerPrimaryTag}>{t('contacts.primary')}</Text>}
                </TouchableOpacity>
              ))}
          </View>
        </TouchableOpacity>
      </RNModal>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  card: {
    backgroundColor: colors.background.surface as string,
    borderRadius: 12,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border as string,
    marginBottom: spacing.md,
  },
  contactRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs },
  contactAvatar: { width: 44, height: 44, borderRadius: 22, backgroundColor: colors.background.secondary as string },
  contactAvatarFallback: { alignItems: 'center', justifyContent: 'center' },
  contactAvatarInitials: { fontSize: typography.fontSize.lg, fontFamily: typography.fontFamily.semibold, color: colors.primary as string },
  contactInfo: { flex: 1 },
  contactName: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },
  metaText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string },

  contactActionsRow: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.xs, marginBottom: spacing.xs },
  contactActionButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.background.secondary as string,
    alignItems: 'center',
    justifyContent: 'center',
  },
  contactActionBadge: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: colors.primary as string,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.white as string,
  },

  quickActionsWrapper: { marginTop: spacing.xs },

  pickerBackdrop: { flex: 1, backgroundColor: 'transparent' },
  pickerCard: {
    position: 'absolute',
    maxWidth: 220,
    backgroundColor: colors.background.surfaceRaised as string,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border as string,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
    padding: spacing.sm,
    minWidth: 160,
  },
  pickerRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, paddingVertical: spacing.xs, paddingHorizontal: spacing.xs / 2 },
  pickerRowText: { flex: 1, fontSize: 13, fontFamily: typography.fontFamily.regular, color: colors.text.primary as string },
  pickerPrimaryTag: { fontSize: 10, fontFamily: typography.fontFamily.medium, color: colors.primary as string },
});
